import { get } from 'lodash';
import { Action } from 'redux';
import { Epic, ofType } from 'redux-observable';
import { EMPTY, forkJoin, from, merge, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { dummyAction } from '../../actions/common';
import { updateGroupAudienceRetargetingCondition } from '../../actions/groupsEdit';
import {
    FETCH_PREDICTOR,
    FETCH_PREDICTOR_MAP,
    FETCH_PREDICTOR_RECOMMENDATION,
    FetchPredictorAction,
    FetchPredictorMapAction,
    FetchPredictorRecommendationAction,
    showPredictorReachError,
    showPredictorRecommendationReachError,
    updateBannersPredictor,
    updatePredictorReach,
    updatePredictorReachMap,
    updatePredictorRecommendationReach
} from '../../actions/predictor';
import { FetchProviderRecommendationResult } from '../../api/predictor/PredictorProvider';
import {
    mapAllPageBlocksToApiFormat,
    mapPageBlocksToApiFormats
} from '../../mappers/predictor';
import { mapRetargetingGoalsFromApiFormat } from '../../mappers/retargeting/retargeting';
import {
    getAudienceMetricsValue,
    getCurrentAudienceTargetingsServerValue
} from '../../selectors/audienceTargetings/audienceTargetings';
import {
    selectEditGroupValueInfo,
    selectPredictorAllPageBlocks,
    selectPredictorGeo,
    selectRateCorrectionsDemography,
    selectRateDevice
} from '../../selectors/groupEdit/group/group';
import { ApiMethodExecutingError } from '../../service/Errors';
import { PlacementsMapHelpers } from '../../service/PlacementsMap/PlacementsMap';
import { getGoalsFromRulesGroups } from '../../service/RetargetingConditionEditorHelpers/RetargetingConditionEditorHelpers';
import { EpicDeps } from '../../service/StoreFactory';
import { MapType } from '../../typings/displayMap';
import { GroupType } from '../../typings/domain';
import { State } from '../../typings/state';

const fetchPredictorEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(FETCH_PREDICTOR),
        mergeMap((action: FetchPredictorAction) => {
            const state = store$.value;
            const {
                commonData: { login }
            } = state;
            const { ids } = selectPredictorGeo(state, action.groupId);
            const platform = selectRateDevice(state, action.groupId);
            const group = state.editGroups.groups[action.groupId];
            const audienceMetrics = getAudienceMetricsValue(
                state,
                action.groupId
            );
            let getGoalsForRetargetingConditions: Observable<Action> = of(
                dummyAction
            );

            const conditions = getCurrentAudienceTargetingsServerValue(
                state,
                action.groupId
            ).conditionRules;

            if (audienceMetrics && !audienceMetrics.goals) {
                getGoalsForRetargetingConditions = from(
                    deps.retargetingDataProvider.getGoalsForRetargetingConditions(
                        login
                    )
                ).pipe(
                    switchMap(data => {
                        const goals = mapRetargetingGoalsFromApiFormat(
                            data.client.goals.rowset
                        );

                        return of(
                            updateGroupAudienceRetargetingCondition(
                                action.groupId,
                                {
                                    ...audienceMetrics,
                                    goals: getGoalsFromRulesGroups(
                                        audienceMetrics.rulesGroups,
                                        goals
                                    )
                                }
                            )
                        );
                    })
                );
            }

            return merge(
                getGoalsForRetargetingConditions,
                from(
                    deps.predictorProvider.getGroupBanners(
                        login,
                        action.campainId,
                        action.groupId
                    )
                ).pipe(
                    map(data => {
                        return updateBannersPredictor(action.groupId, data);
                    }),
                    catchError(() => EMPTY)
                ),
                from(
                    deps.predictorProvider.fetchPredictorData(
                        action.campainId,
                        action.groupId,
                        group.value.groupType,
                        ids,
                        group.isNew,
                        platform,
                        conditions
                    )
                ).pipe(
                    map(response => {
                        const { id, detailed, basic } = response;
                        const data = {
                            groupId: action.groupId,
                            reqId: id,
                            basic,
                            detailed
                        };

                        return updatePredictorReach(data);
                    }),
                    catchError(
                        (
                            error: ApiMethodExecutingError
                        ): Observable<Action> => {
                            return of(
                                showPredictorReachError(
                                    action.groupId,
                                    error.getNativeError().message
                                )
                            );
                        }
                    )
                )
            );
        }),
        catchError(() => EMPTY)
    );
};

const fetchPredictorRecommendationEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(FETCH_PREDICTOR_RECOMMENDATION),
        mergeMap((action: FetchPredictorRecommendationAction) => {
            const state = store$.value;
            const {
                commonData: { login }
            } = state;
            const { ids } = selectPredictorGeo(state, action.groupId);
            const platform = selectRateDevice(state, action.groupId);
            const group = state.editGroups.groups[action.groupId];

            const conditions = getCurrentAudienceTargetingsServerValue(
                state,
                action.groupId
            ).conditionRules;

            return merge(
                from(
                    deps.predictorProvider.getGroupBanners(
                        login,
                        action.campainId,
                        action.groupId
                    )
                ).pipe(
                    map(data => {
                        return updateBannersPredictor(action.groupId, data);
                    }),
                    catchError(() => EMPTY)
                ),
                from(
                    deps.predictorProvider.fetchPredictorRecommendationData(
                        action.campainId,
                        action.groupId,
                        group.value.groupType,
                        ids,
                        group.isNew,
                        platform,
                        conditions
                    )
                ).pipe(
                    map((response: FetchProviderRecommendationResult) => {
                        const { bannersFormats } = response;

                        return updatePredictorRecommendationReach(
                            action.groupId,
                            bannersFormats
                        );
                    }),
                    catchError(
                        (
                            error: ApiMethodExecutingError
                        ): Observable<Action> => {
                            return of(
                                showPredictorRecommendationReachError(
                                    action.groupId,
                                    error.getNativeError().message
                                )
                            );
                        }
                    )
                )
            );
        })
    );
};

const fetchPredictorMapEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(FETCH_PREDICTOR_MAP),
        mergeMap((action: FetchPredictorMapAction) => {
            const state = store$.value;
            const groupType = action.groupType;
            const group = state.editGroups.groups[action.groupId];
            const groupValue = selectEditGroupValueInfo(state, action.groupId);
            let fetchWithAllPageBlocks;
            let fetchWithCheckPageBlocks;

            if (groupType === GroupType.CPM_OUTDOOR) {
                const allPageBlocks =
                    selectPredictorAllPageBlocks(state, MapType.outdoor) || {};

                const checkedPageBlocks = PlacementsMapHelpers.outdoorCheckedByRegionToPageBlocks(
                    allPageBlocks,
                    PlacementsMapHelpers.getOutdoorCheckedByRegion(
                        allPageBlocks,
                        get(groupValue, 'outdoorMap.pageBlocks')
                    )
                );

                fetchWithAllPageBlocks = deps.predictorProvider.fetchPredictorOutdoorData(
                    action.campainId,
                    action.groupId,
                    group.isNew,
                    mapAllPageBlocksToApiFormat(allPageBlocks)
                );

                fetchWithCheckPageBlocks = deps.predictorProvider.fetchPredictorOutdoorData(
                    action.campainId,
                    action.groupId,
                    group.isNew,
                    mapPageBlocksToApiFormats(checkedPageBlocks)
                );
            } else {
                const allPageBlocks =
                    selectPredictorAllPageBlocks(state, MapType.indoor) || {};

                const adjustment = selectRateCorrectionsDemography(
                    state,
                    action.groupId
                );

                const checkedPageBlocks = PlacementsMapHelpers.indoorCheckedByRegionToPageBlocks(
                    allPageBlocks,
                    PlacementsMapHelpers.getIndoorCheckedByRegion(
                        allPageBlocks,
                        get(groupValue, 'indoorMap.pageBlocks')
                    )
                );

                fetchWithAllPageBlocks = deps.predictorProvider.fetchPredictorIndoorData(
                    action.campainId,
                    action.groupId,
                    group.isNew,
                    mapAllPageBlocksToApiFormat(allPageBlocks)
                );

                fetchWithCheckPageBlocks = deps.predictorProvider.fetchPredictorIndoorData(
                    action.campainId,
                    action.groupId,
                    group.isNew,
                    mapPageBlocksToApiFormats(checkedPageBlocks),
                    adjustment
                );
            }

            return forkJoin(
                fetchWithCheckPageBlocks,
                fetchWithAllPageBlocks
            ).pipe(
                map(([detailedResult, basicResult]) => {
                    if (!detailedResult || !basicResult) {
                        throw new Error();
                    }

                    const result = {
                        groupId: action.groupId,
                        reqId: detailedResult.id,
                        basic: basicResult.data,
                        detailed: detailedResult.data,
                        otsDetailed: detailedResult.otsData,
                        otsBasic: basicResult.otsData
                    };

                    return updatePredictorReachMap(result);
                }),
                catchError(
                    (error: ApiMethodExecutingError): Observable<Action> => {
                        return of(
                            showPredictorReachError(
                                action.groupId,
                                error.getNativeError().message
                            )
                        );
                    }
                )
            );
        })
    );
};

export {
    fetchPredictorEpic,
    fetchPredictorRecommendationEpic,
    fetchPredictorMapEpic
};
